// The card is shared by the roster and the Archive, which render the same
// accepted character in two different states. Keeping one implementation is
// what stops the archived treatment drifting away from the live one. Drafts
// have their own card under screens/drafts.
import { Link } from "react-router-dom";
import { Skeleton, SkeletonStatus } from "@/components/ui/loading";
import { LifecycleBadge, LocalMedia } from "@/components/StudioPrimitives";
import { useActiveWorkspace } from "@/lib/workspace/ActiveWorkspace";
import type { CharacterCard } from "@/lib/studio/types";
import { cn } from "@/lib/cn";

export function CharacterCardView({
  character,
  orgSlug,
  restorationKey,
}: {
  character: CharacterCard;
  orgSlug: string;
  restorationKey: string;
}) {
  const { links } = useActiveWorkspace();
  const name = character.name || "Untitled character";
  return (
    <Link
      to={links.character(character.id)}
      id={`character-card-${character.id}`}
      onClick={() => {
        sessionStorage.setItem(`${restorationKey}:selected`, character.id);
      }}
      className="group overflow-hidden rounded-xl border border-border bg-bg-elevated transition-colors hover:border-border-strong motion-reduce:transition-none"
      aria-label={`${name}, ${character.lifecycle}`}
    >
      <div className="aspect-[3/4] overflow-hidden bg-bg-sunken">
        <CardPortrait character={character} orgSlug={orgSlug} />
      </div>
      <div className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate font-semibold">{name}</h2>
            <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-fg-muted">
              {character.persona || "Persona not written yet"}
            </p>
          </div>
          <LifecycleBadge state={character.lifecycle} />
        </div>
        <p className="text-[12px] text-fg-muted">
          {character.lifecycle === "archived"
            ? "Archived — history remains available"
            : (character.accepted_label ?? "Accepted version")}
        </p>
        <CardCounts character={character} />
        <div className="border-t border-border-faint pt-3 font-mono text-[10px] text-fg-subtle">
          {new Date(character.updated_at).toLocaleString()}
        </div>
      </div>
    </Link>
  );
}

const placeholderClass =
  "flex size-full flex-col items-center justify-center gap-2 px-4 text-center font-mono text-[11px] uppercase tracking-wider text-fg-subtle";

/**
 * The card picture.
 *
 * `thumbnail_media_id` is the accepted identity's front proof view, chosen
 * server-side and never a generated image: a character whose face is one of
 * its own outputs tells the reader something false about what it is
 * (design 3.8). So a character whose identity has no front view gets a
 * placeholder rather than a borrowed picture.
 */
export function CardPortrait({
  character,
  orgSlug,
}: {
  character: CharacterCard;
  orgSlug: string;
}) {
  const archived = character.lifecycle === "archived";
  if (character.thumbnail_media_id) {
    return (
      <div className="relative size-full">
        <LocalMedia
          orgSlug={orgSlug}
          mediaID={character.thumbnail_media_id}
          alt=""
          thumbnail
          className={cn(
            "size-full object-top transition-transform duration-300 group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100",
            archived && "opacity-40",
          )}
        />
        {archived && (
          <span className="absolute inset-x-0 bottom-0 bg-bg/80 py-1.5 text-center font-mono text-[10px] uppercase tracking-wider text-fg-muted">
            Archived
          </span>
        )}
      </div>
    );
  }
  if (archived) {
    return <div className={placeholderClass}>Archived · no identity view</div>;
  }
  return <div className={placeholderClass}>No identity view</div>;
}

/**
 * Generated media and identity views, on two lines, never added together:
 * proof is what the character IS and library media is what it HAS MADE
 * (design 3.8). The card carries one library total covering images and
 * videos, so it names library items, not a kind. Zero is written in words,
 * because "0 items" reads as a defect rather than as a character nobody has
 * generated from yet.
 */
export function CardCounts({ character }: { character: CharacterCard }) {
  const generated = character.media_count ?? 0;
  const views = character.identity_view_count ?? 0;
  return (
    <dl className="space-y-0.5 text-[12px] text-fg-muted">
      <div>
        <dt className="sr-only">Generated media</dt>
        <dd>
          {generated > 0
            ? `${generated} library ${generated === 1 ? "item" : "items"}`
            : "nothing generated yet"}
        </dd>
      </div>
      <div>
        <dt className="sr-only">Identity views</dt>
        <dd>
          {views > 0
            ? `${views} identity ${views === 1 ? "view" : "views"}`
            : "no identity views"}
        </dd>
      </div>
    </dl>
  );
}

export function CharacterGridSkeleton({
  label = "Loading characters…",
}: {
  label?: string;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2, 3, 4, 5].map((item) => (
        <div
          key={item}
          className="overflow-hidden rounded-xl border border-border bg-bg-elevated"
        >
          <Skeleton className="aspect-[3/4] rounded-none" />
          <div className="space-y-3 p-4">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-9" />
            <Skeleton className={cn("h-4 w-1/2", item % 2 === 0 && "w-1/3")} />
            <div className="space-y-1">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-2/5" />
            </div>
          </div>
        </div>
      ))}
      <SkeletonStatus label={label} />
    </div>
  );
}
